/* The nine headline metrics — STORY-024.
 *
 * ── every figure states its basis, and never in a tooltip ───────────────────
 * "Average profit (9 events)" is honest; "Average profit" alone is how a wrong
 * number survives for months. The basis is ordinary text under the figure — a
 * tooltip is not available to a keyboard, a screen reader, a phone, or a
 * printout, and this is the screen somebody screenshots and sends to a partner.
 *
 * ── two charts, and each has a table twin ───────────────────────────────────
 * The diverging bar makes "which nights made money" and the spread the average
 * sits in legible at a glance; the meter makes an overrun show as length. Both
 * are HTML and CSS with no library and no canvas: at this size a div with a
 * width is the honest implementation, and it stays selectable, printable and
 * readable aloud. A canvas is a picture of a number.
 *
 * ── colour is never the signal ──────────────────────────────────────────────
 * On the bars the SIDE of the zero baseline carries the sign, every bar is
 * labelled with its own value, and a loss is worded. The diverging pair is
 * redundant encoding on top of that — which is the only kind worth having, and
 * it was still computed rather than chosen: the obvious green-and-red separates
 * by ΔE 4.2 under deuteranopia. See `design/tokens/color.css`.
 *
 * ── nothing is summed across currencies ─────────────────────────────────────
 * One chart and one set of money tiles per currency. A combined figure would
 * need a rate this product does not have and would not know the date of.
 */

import type { DashboardResponse } from '@eventuley/contracts';
import { formatMoney, money, parseCurrency } from '@eventuley/domain';
import { Link } from '@tanstack/react-router';
import { NetworkError } from '../../../shared/api/client.js';
import { useI18n, useTranslate } from '../../../shared/i18n/index.js';
import { Callout } from '../../../shared/ui/index.js';
import { useDashboard } from '../api/index.js';

export function Dashboard({ organisationId }: { readonly organisationId: string }) {
  const t = useTranslate();
  const { formatLocale: locale } = useI18n();
  const { data, error, isLoading } = useDashboard(organisationId);

  const format = (minor: number, code: string) => {
    const parsed = parseCurrency(code);
    return parsed ? formatMoney(money(minor, parsed), locale) : `${minor} ${code}`;
  };

  /**
   * A count, in the reader's own separators.
   *
   * `String(n)` renders 1500 as "1500" where Turkish wants "1.500" —
   * `i18n.md` hard rule 3 forbids formatting a number by hand, and a count is
   * a number. Small counts hide this, which is exactly why it is easy to ship.
   */
  const count = (value: number) => new Intl.NumberFormat(locale).format(value);

  /** A negative figure said in words. Colour is not enough — `design.md`. */
  const signed = (minor: number, code: string) =>
    minor < 0 ? t('metrics.down', { amount: format(Math.abs(minor), code) }) : format(minor, code);

  if (error) {
    return (
      <section className="settings__section">
        <h2>{t('metrics.heading')}</h2>
        <Callout tone="danger">
          {error instanceof NetworkError ? t('error.network') : t('error.unexpected')}
        </Callout>
      </section>
    );
  }

  if (isLoading || !data) {
    return (
      <section className="settings__section">
        <h2>{t('metrics.heading')}</h2>
        <p className="field__hint">{t('common.loading')}</p>
      </section>
    );
  }

  return (
    <section className="settings__section">
      <h2>{t('metrics.heading')}</h2>

      <ul className="metrics">
        <Tile
          label={t('metrics.total_events')}
          value={count(data.counts.total)}
          basis={t('metrics.total_events_basis', {
            upcoming: data.counts.upcoming,
            completed: data.counts.completed,
            other: data.counts.uncounted,
          })}
        />
        <Tile
          label={t('metrics.upcoming_events')}
          value={count(data.counts.upcoming)}
          /* Says which statuses it counted, because "upcoming" is a judgement
             and an idea deliberately is not one. */
          basis={t('metrics.upcoming_events_basis')}
        />
        <Tile
          label={t('metrics.completed_events')}
          value={count(data.counts.completed)}
          basis={t('metrics.completed_events_basis')}
        />
      </ul>

      {/* Income, expense and profit over EVERYTHING, per currency.
      
          Not the sum of the per-Event figures below: those exclude overhead
          and exclude cancelled Events, so their sum is a smaller number that
          looks exactly like a total. The profit here is the same figure the
          "How it adds up" tab calls the organisation total, and a test asserts
          the two agree — two tabs of one screen disagreeing by exactly the
          overhead is the spreadsheet defect this product exists to fix. */}
      {data.totals.map((total) => (
        <ul className="metrics" key={`totals-${total.currency}`}>
          <Tile
            label={t('metrics.total_income', { currency: total.currency })}
            value={format(total.incomeMinor, total.currency)}
            basis={t('metrics.total_income_basis')}
          />
          <Tile
            label={t('metrics.total_expense', { currency: total.currency })}
            value={format(total.expenseMinor, total.currency)}
            basis={t('metrics.total_expense_basis')}
          />
          <Tile
            label={t('metrics.total_profit', { currency: total.currency })}
            value={signed(total.profitMinor, total.currency)}
            basis={t('metrics.total_profit_basis')}
          />
        </ul>
      ))}

      {data.averageProfit.length === 0 ? (
        /* Zero events that ran, so no average is attempted at all — "none we
           ran" and "the ones we ran averaged nothing" are different facts. */
        <p className="field__hint">{t('metrics.nothing_yet')}</p>
      ) : (
        data.averageProfit.map((average) => (
          <ul className="metrics" key={`average-${average.currency}`}>
            <Tile
              label={t('metrics.average_profit', { currency: average.currency })}
              value={
                average.averageMinor === null
                  ? t('metrics.not_available')
                  : signed(average.averageMinor, average.currency)
              }
              /* The count IS the metric. Without it this figure was wrong for
                 months and nobody could see it. And it is a DIFFERENT total
                 from the one above — attributed only, cancelled Events left
                 out — so it says so rather than being read as that one
                 divided. */
              basis={t('metrics.average_profit_basis', { count: average.eventCount })}
            />
            <Tile
              label={t('metrics.attributed_profit', { currency: average.currency })}
              value={signed(average.totalMinor, average.currency)}
              basis={t('metrics.attributed_profit_basis')}
            />
          </ul>
        ))
      )}

      {data.mostProfitable.map((best) => (
        <MostProfitableTile
          key={`best-${best.currency}`}
          organisationId={organisationId}
          best={best}
          signed={signed}
        />
      ))}

      <ProfitByEvent data={data} signed={signed} />
      <Utilisation data={data} format={format} />
    </section>
  );
}

function Tile({
  label,
  value,
  basis,
}: {
  readonly label: string;
  readonly value: string;
  readonly basis: string;
}) {
  return (
    <li className="metric">
      <span className="metric__label">{label}</span>
      <strong className="metric__value">{value}</strong>
      <span className="metric__basis">{basis}</span>
    </li>
  );
}

/**
 * The best night, and every night tied with it.
 *
 * Linked, because the story asks for it and because a name without a way to
 * open it is a fact the reader has to go and look up.
 */
function MostProfitableTile({
  organisationId,
  best,
  signed,
}: {
  readonly organisationId: string;
  readonly best: DashboardResponse['mostProfitable'][number];
  readonly signed: (minor: number, code: string) => string;
}) {
  const t = useTranslate();

  return (
    <ul className="metrics">
      <li className="metric">
        <span className="metric__label">
          {t('metrics.most_profitable', { currency: best.currency })}
        </span>
        <strong className="metric__value">
          {best.events.map((event, index) => (
            <span key={event.eventId}>
              {index > 0 ? t('metrics.tie_separator') : null}
              <Link
                to="/orgs/$organisationId/events/$eventId"
                params={{ organisationId, eventId: event.eventId }}
              >
                {event.name}
              </Link>
            </span>
          ))}
        </strong>
        <span className="metric__basis">
          {/* A tie is stated, not implied by there being two names. */}
          {best.events.length > 1
            ? t('metrics.most_profitable_tie', {
                count: best.events.length,
                amount: signed(best.profitMinor, best.currency),
              })
            : t('metrics.most_profitable_basis', {
                amount: signed(best.profitMinor, best.currency),
              })}
        </span>
      </li>
    </ul>
  );
}

/**
 * Profit by Event — a diverging bar, one chart per currency.
 *
 * Bars run either side of a zero baseline, so which nights made money and
 * which lost it is legible at a glance and the spread the average sits in
 * becomes visible. That is what makes "most profitable event" more than a name.
 *
 * Every row carries its Event's name and its own figure as text. **That is the
 * table twin the story asks for** — not a toggle to a second view, but the same
 * markup being readable without the bars: a screen reader reads name and value,
 * a printout shows both, and the bar adds the shape. A chart whose values live
 * only in a tooltip has no twin at all.
 */
function ProfitByEvent({
  data,
  signed,
}: {
  readonly data: DashboardResponse;
  readonly signed: (minor: number, code: string) => string;
}) {
  const t = useTranslate();

  const rows = data.eventProfits;
  if (rows.length === 0) return null;

  /* One chart per currency, and each has its own scale. A shared scale across
     currencies would imply a comparison that needs a rate this product does
     not have. */
  const currencies = [...new Set(rows.map((row) => row.currency))];

  return (
    <div className="chart">
      <p className="chart__caption">{t('metrics.chart_profit_caption')}</p>
      {currencies.map((currency) => {
        const inCurrency = rows.filter((row) => row.currency === currency);
        /* The scale runs to the largest ABSOLUTE value, so both halves share
           one scale and a bar's length is comparable across the baseline. */
        const widest = Math.max(...inCurrency.map((row) => Math.abs(row.profitMinor)), 1);

        return (
          <div className="diverging" key={`chart-${currency}`}>
            {inCurrency.map((row) => {
              const share = (Math.abs(row.profitMinor) / widest) * 100;
              const loss = row.profitMinor < 0;

              return (
                <div className="diverging__row" key={`bar-${row.eventId}-${currency}`}>
                  <span className="diverging__name">{row.name}</span>
                  <span className="diverging__plot">
                    <span
                      className={`diverging__bar ${
                        loss ? 'diverging__bar--loss' : 'diverging__bar--profit'
                      }`}
                      style={{ inlineSize: `${share}%` }}
                    />
                  </span>
                  {/* Direct-labelled, never a tooltip: a value only a hover can
                      reach is one a phone and a printout do not have. */}
                  <span className="diverging__value">{signed(row.profitMinor, currency)}</span>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Budget utilisation — a meter, per currency.
 *
 * The track runs to whichever is larger, the limit or the actual, so an
 * overrun shows as **length** rather than as a bar pinned at full. A bar that
 * cannot exceed its track cannot show the one thing worth seeing.
 */
function Utilisation({
  data,
  format,
}: {
  readonly data: DashboardResponse;
  readonly format: (minor: number, code: string) => string;
}) {
  const t = useTranslate();
  const { formatLocale: locale } = useI18n();

  if (data.utilisation.length === 0) return null;

  return (
    <div className="chart">
      <p className="chart__caption">{t('metrics.chart_budget_caption')}</p>
      {data.utilisation.map((entry) => {
        if (entry.budgetMinor === null) {
          /* "Not set" and "0% used" are different facts and the second is a lie
             about the first, so no division happens in this branch at all. */
          return (
            <p className="field__hint" key={`meter-${entry.currency}`}>
              {t('metrics.budget_not_set', {
                currency: entry.currency,
                spent: format(entry.spentMinor, entry.currency),
              })}
            </p>
          );
        }

        const { budgetMinor, spentMinor } = entry;
        /* Computed here, once, from the two integers — a percentage rounded on
           the server and again on the client is rounded twice, invisibly. */
        const percent =
          budgetMinor > 0
            ? new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(
                spentMinor / budgetMinor,
              )
            : null;

        const scale = Math.max(budgetMinor, spentMinor, 1);
        const over = spentMinor > budgetMinor;

        return (
          <div className="meter" key={`meter-${entry.currency}`}>
            <div
              className="meter__track"
              /* DECORATION, and deliberately so. The sentence below carries the
                 whole figure — the share, the amount, the budget it is measured
                 against, and the overrun — which is more than a meter role can
                 say and is the thing the story actually asks for: a labelled
                 figure, never a bare number.
              
                 A `role="meter"` here would announce a number with no
                 denominator, and a native `<meter>` is drawn by the OS, which
                 `design.md` warns off precisely because CSS cannot reach
                 inside it. */
              aria-hidden="true"
            >
              {/* The fill is the SPEND against a scale that runs to whichever
                  is larger. When spending has overrun, the fill runs past the
                  mark and the difference is visible as length — the previous
                  version made the track and the fill both 100% whenever spend
                  reached the budget, so ₺3,000 against ₺1,000 rendered
                  pixel-identically to ₺1,000 against ₺1,000, differing only in
                  colour. Which is exactly the case colour may not carry. */}
              <div
                className={`meter__fill ${over ? 'meter__fill--over' : ''}`}
                style={{ inlineSize: `${(spentMinor / scale) * 100}%` }}
              />
              <span
                className="meter__mark"
                style={{ insetInlineStart: `${(budgetMinor / scale) * 100}%` }}
              />
            </div>
            <p className="meter__labels">
              <span>
                {percent === null
                  ? t('metrics.budget_zero', { currency: entry.currency })
                  : t('metrics.budget_used', {
                      currency: entry.currency,
                      percent,
                      spent: format(spentMinor, entry.currency),
                      budget: format(budgetMinor, entry.currency),
                    })}
              </span>
              {/* An overrun in words as well as in length and colour. */}
              {over ? (
                <span>
                  {t('metrics.budget_over', {
                    amount: format(spentMinor - budgetMinor, entry.currency),
                  })}
                </span>
              ) : null}
            </p>
          </div>
        );
      })}
    </div>
  );
}
